/**
 * Glassmorphism / Liquid Glass Configuration
 * Aligned with iOS 26 / macOS 26 design language.
 *
 * Key differences from classic glassmorphism:
 * - Higher blur (40-60px) for that "frosted" depth
 * - Subtle tint instead of white overlay
 * - Thin 0.5px borders (or 1px very low opacity)
 * - Reduced saturation for organic feel
 */

export interface GlassmorphismConfig {
  background: string;
  backgroundOpacity: number;
  blur: number;
  saturation: number;
  borderColor: string;
  borderWidth: number;
  borderOpacity: number;
  shadowColor: string;
  shadowOpacity: number;
  shadowBlur: number;
  shadowSpread: number;
  insetShadowColor: string;
  insetShadowOpacity: number;
  borderRadius: number;
  transitionDuration: string;
}

export const glassmorphismConfigs = {
  light: {
    background: 'rgba(255, 255, 255, 0.55)',
    backgroundOpacity: 0.55,
    blur: 40,
    saturation: 120,
    borderColor: 'rgba(255, 255, 255, 0.45)',
    borderWidth: 0.5,
    borderOpacity: 0.45,
    shadowColor: 'rgba(0, 0, 0, 0.08)',
    shadowOpacity: 0.08,
    shadowBlur: 32,
    shadowSpread: 0,
    insetShadowColor: 'rgba(255, 255, 255, 0.5)',
    insetShadowOpacity: 0.5,
    borderRadius: 18,
    transitionDuration: '0.25s',
  } as GlassmorphismConfig,

  dark: {
    background: 'rgba(28, 28, 46, 0.45)',
    backgroundOpacity: 0.45,
    blur: 40,
    saturation: 120,
    borderColor: 'rgba(255, 255, 255, 0.08)',
    borderWidth: 0.5,
    borderOpacity: 0.08,
    shadowColor: 'rgba(0, 0, 0, 0.5)',
    shadowOpacity: 0.5,
    shadowBlur: 32,
    shadowSpread: 0,
    insetShadowColor: 'rgba(255, 255, 255, 0.06)',
    insetShadowOpacity: 0.06,
    borderRadius: 18,
    transitionDuration: '0.25s',
  } as GlassmorphismConfig,
};

/**
 * Creates glassmorphism CSS-in-JS styles from config
 */
export const createGlassmorphismStyles = (config: GlassmorphismConfig) => {
  const border = `${config.borderWidth}px solid ${config.borderColor}`;
  const shadow = `0 ${config.shadowBlur / 4}px ${config.shadowBlur}px 0 ${config.shadowColor}, 0 0 0 1px ${config.insetShadowColor} inset`;

  return {
    background: config.background,
    backdropFilter: `blur(${config.blur}px) saturate(${config.saturation}%)`,
    WebkitBackdropFilter: `blur(${config.blur}px) saturate(${config.saturation}%)`,
    borderRadius: `${config.borderRadius}px`,
    border,
    boxShadow: shadow,
    transition: `all ${config.transitionDuration} ease`,
  };
};

/**
 * Pre-built variants for each mode
 */
export const glassmorphismVariants = {
  light: {
    default: createGlassmorphismStyles(glassmorphismConfigs.light),
    subtle: createGlassmorphismStyles({
      ...glassmorphismConfigs.light,
      background: 'rgba(255, 255, 255, 0.3)',
      backgroundOpacity: 0.3,
    }),
    strong: createGlassmorphismStyles({
      ...glassmorphismConfigs.light,
      background: 'rgba(255, 255, 255, 0.7)',
      backgroundOpacity: 0.7,
      blur: 60,
    }),
    card: createGlassmorphismStyles({
      ...glassmorphismConfigs.light,
      borderRadius: 20,
      shadowBlur: 40,
      shadowSpread: -4,
    }),
    panel: createGlassmorphismStyles({
      ...glassmorphismConfigs.light,
      borderRadius: 14,
    }),
  },
  dark: {
    default: createGlassmorphismStyles(glassmorphismConfigs.dark),
    subtle: createGlassmorphismStyles({
      ...glassmorphismConfigs.dark,
      background: 'rgba(28, 28, 46, 0.25)',
      backgroundOpacity: 0.25,
    }),
    strong: createGlassmorphismStyles({
      ...glassmorphismConfigs.dark,
      background: 'rgba(28, 28, 46, 0.6)',
      backgroundOpacity: 0.6,
      blur: 60,
    }),
    card: createGlassmorphismStyles({
      ...glassmorphismConfigs.dark,
      borderRadius: 20,
      shadowBlur: 40,
      shadowSpread: -4,
    }),
    panel: createGlassmorphismStyles({
      ...glassmorphismConfigs.dark,
      borderRadius: 14,
    }),
  },
};
